import { useState } from 'react';
import { trackEvent } from '../analytics.ts';

/**
 * 3-question plan recommender. Sits above the pricing plan grid and:
 *   1. Fights paradox-of-choice (Schwartz 2004) — many SMB visitors freeze when shown 3 plans
 *      with different price/credit/feature combos. The wizard reduces decisions from "choose
 *      among 3 plans" to "answer 3 simple questions"
 *   2. Default bias (Johnson/Goldstein) — the recommendation becomes the new default, which
 *      research shows is 2-3× more likely to be selected
 *   3. Commitment-consistency (Cialdini) — answering the questions creates a small commitment
 *      that nudges the user toward acting on the recommendation
 *
 * Ethical: NO defaults pre-selected, NO forced answer order, recommendation is honestly
 * derived from inputs. User can ignore the recommendation entirely and pick any plan.
 */

export type PlanCode = 'pkg_single_scan' | 'sub_basic' | 'sub_agency';

interface Question {
  id: 'frequency' | 'sites' | 'branding';
  label: string;
  options: Array<{ value: string; label: string; weight: { single: number; basic: number; agency: number } }>;
}

const QUESTIONS: Question[] = [
  {
    id: 'frequency',
    label: 'How often will you re-scan?',
    options: [
      { value: 'once',    label: 'Once — one-off compliance check',  weight: { single: 3, basic: 0, agency: 0 } },
      { value: 'monthly', label: 'Monthly — after each release',     weight: { single: 0, basic: 3, agency: 1 } },
      { value: 'weekly',  label: 'Weekly+ — continuous monitoring',   weight: { single: 0, basic: 1, agency: 3 } },
    ],
  },
  {
    id: 'sites',
    label: 'How many sites do you audit?',
    options: [
      { value: '1',   label: '1 site',           weight: { single: 3, basic: 1, agency: 0 } },
      { value: '2-5', label: '2 to 5 sites',     weight: { single: 0, basic: 3, agency: 1 } },
      { value: '6+',  label: '6 or more sites',   weight: { single: 0, basic: 0, agency: 3 } },
    ],
  },
  {
    id: 'branding',
    label: 'Do client-facing reports need your branding?',
    options: [
      { value: 'no',  label: 'No — internal use only',                       weight: { single: 1, basic: 1, agency: 0 } },
      { value: 'yes', label: 'Yes — I deliver these to clients (agency)',     weight: { single: 0, basic: 0, agency: 4 } },
    ],
  },
];

type Answers = Partial<Record<Question['id'], string>>;

const PLAN_META: Record<PlanCode, { name: string; price: string; reason: (a: Answers) => string }> = {
  pkg_single_scan: {
    name: 'Single scan',
    price: '€29 one-time',
    reason: () =>
      "A one-time audit is the right fit when you're not running scans on a recurring basis. No subscription commitment.",
  },
  sub_basic: {
    name: 'Basic',
    price: '€9/month for 5 scans',
    reason: () =>
      'You re-audit after releases and have a handful of sites — Basic covers 5 scans per month, which is the sweet spot for product teams shipping weekly.',
  },
  sub_agency: {
    name: 'Agency',
    price: '€49/month for 50 scans',
    reason: () =>
      'For 6+ sites or client-facing reports, Agency gives you 50 monthly scans, branded PDF output, and the depth a freelance/consultancy operation needs.',
  },
};

function pickPlan(answers: Answers): PlanCode | null {
  const allAnswered = QUESTIONS.every((q) => answers[q.id] !== undefined);
  if (!allAnswered) return null;

  const scores = { single: 0, basic: 0, agency: 0 };
  for (const q of QUESTIONS) {
    const opt = q.options.find((o) => o.value === answers[q.id]);
    if (!opt) continue;
    scores.single += opt.weight.single;
    scores.basic += opt.weight.basic;
    scores.agency += opt.weight.agency;
  }

  // Tie-break order: agency wins on ties (highest plan + most likely strong fit), then basic.
  const ranked = (
    [
      ['sub_agency', scores.agency],
      ['sub_basic', scores.basic],
      ['pkg_single_scan', scores.single],
    ] as Array<[PlanCode, number]>
  ).sort((a, b) => b[1] - a[1]);

  return ranked[0][0];
}

export interface PlanRecommenderProps {
  /** Called when the recommendation is computed — used by PricingPage to highlight the
      matching plan card. */
  onRecommendation: (planCode: PlanCode | null) => void;
}

export default function PlanRecommender({ onRecommendation }: PlanRecommenderProps) {
  const [answers, setAnswers] = useState<Answers>({});
  const [expanded, setExpanded] = useState(true);

  function answer(qid: Question['id'], value: string) {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    const plan = pickPlan(next);
    if (plan) {
      trackEvent('plan_recommender_complete', {
        plan,
        ...next,
      });
    }
    onRecommendation(plan);
  }

  const recommendation = pickPlan(answers);
  const allAnswered = recommendation !== null;

  if (!expanded) {
    return (
      <div className="plan-rec-collapsed">
        <button
          type="button"
          className="link-button"
          onClick={() => setExpanded(true)}
        >
          Not sure which plan? Take the 30-second recommender →
        </button>
      </div>
    );
  }

  return (
    <section className="plan-rec" aria-labelledby="plan-rec-heading">
      <header className="plan-rec-head">
        <p className="plan-rec-eyebrow">30-second recommender</p>
        <h2 id="plan-rec-heading">Not sure which plan fits?</h2>
        <button
          type="button"
          className="plan-rec-dismiss"
          onClick={() => {
            setExpanded(false);
            setAnswers({});
            onRecommendation(null);
          }}
          aria-label="Skip the recommender"
        >
          Skip
        </button>
      </header>

      <div className="plan-rec-questions">
        {QUESTIONS.map((q) => (
          <fieldset key={q.id} className="plan-rec-q">
            <legend>{q.label}</legend>
            <div className="plan-rec-opts" role="radiogroup">
              {q.options.map((opt) => {
                const selected = answers[q.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`plan-rec-opt${selected ? ' is-selected' : ''}`}
                    onClick={() => answer(q.id, opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {allAnswered && recommendation && (
        <div className="plan-rec-result" role="status" aria-live="polite">
          <p className="plan-rec-result-label">Best fit for you</p>
          <p className="plan-rec-result-name">{PLAN_META[recommendation].name}</p>
          <p className="plan-rec-result-price">{PLAN_META[recommendation].price}</p>
          <p className="plan-rec-result-why">{PLAN_META[recommendation].reason(answers)}</p>
          <p className="plan-rec-result-note muted small">
            We've highlighted this plan below — but you can pick any. Cancel anytime, 7-day
            money-back on every plan.
          </p>
        </div>
      )}
    </section>
  );
}
